import { Controller } from "@hotwired/stimulus"

// The currency select fused to a payment amount (owner directive
// 2026-09-09: EUR payments, stored in USD). The browser only DISPLAYS: the
// server derives the stored USD from the typed EUR, the ECB rate, and the
// PayPal markup, with the same half-up rounding mirrored here for the
// estimate the operator sees. In EUR mode the USD input is disabled (not
// submitted) and carries the live estimate so the split-line math keeps
// working in USD; "Enter the exact USD instead" re-enables it for PayPal's
// receipt figure and flags fx_confirm. A curator whose saved currency is
// EUR preselects EUR while the amount is still blank (fx-amount:preselect).
export default class extends Controller {
  static targets = ["currency", "usd", "foreign", "foreignBox", "rate", "source", "rateDate", "confirm", "note", "receiptLink"]
  static values = {
    rate: String, rateDate: String, markup: String, typeSelector: String,
    currency: { type: String, default: "USD" }, confirmed: { type: String, default: "0" }
  }

  connect() {
    this.receipt = this.confirmedValue === "1"
    this.typedRate = this.hasRateTarget && this.rateTarget.value !== "" && this.rateTarget.value !== this.rateValue
    this.preselectHandler = (e) => this.preselect(e.detail?.currency)
    this.element.addEventListener("fx-amount:preselect", this.preselectHandler)
    this.typeHandler = () => this.typeChanged()
    this.typeSelect()?.addEventListener("change", this.typeHandler)
    this.apply()
  }

  disconnect() {
    this.element.removeEventListener("fx-amount:preselect", this.preselectHandler)
    this.typeSelect()?.removeEventListener("change", this.typeHandler)
  }

  typeSelect() {
    const form = this.element.closest("form")
    return form ? form.querySelector(this.typeSelectorValue) : null
  }

  foreign() { return this.currencyTarget.value !== "USD" }
  zeroType() { return ["free_placement", "playlist_trade", "submithub"].includes(this.typeSelect()?.value) }
  paypal() { return this.typeSelect()?.value === "paypal" }

  currencyChanged() {
    if (this.foreign() && this.zeroType()) this.currencyTarget.value = "USD"
    this.apply()
  }

  typeChanged() {
    if (this.foreign() && this.zeroType()) this.currencyTarget.value = "USD"
    this.apply()
  }

  preselect(currency) {
    if (!currency || currency === this.currencyTarget.value) return
    if (this.usdTarget.value && parseFloat(this.usdTarget.value) > 0) return
    if (this.foreignTarget.value && parseFloat(this.foreignTarget.value) > 0) return
    if (!Array.from(this.currencyTarget.options).some(o => o.value === currency)) return
    this.currencyTarget.value = currency
    this.apply()
  }

  foreignChanged() { this.recompute() }
  rateChanged() {
    this.typedRate = this.rateTarget.value !== this.rateValue
    this.sourceTarget.value = this.typedRate ? "manual" : "ecb"
    this.recompute()
  }
  usdChanged() { if (this.foreign() && this.receipt) this.paintNote() }

  toggleReceipt() {
    this.receipt = !this.receipt
    if (this.receipt) this.usdTarget.value = ""
    this.apply()
    if (this.receipt) this.usdTarget.focus()
  }

  apply() {
    const foreign = this.foreign()
    this.foreignTarget.hidden = !foreign
    this.foreignTarget.disabled = !foreign
    this.foreignBoxTarget.hidden = !foreign
    ;[this.rateTarget, this.sourceTarget, this.rateDateTarget, this.confirmTarget].forEach(el => { el.disabled = !foreign })
    if (!foreign) {
      this.receipt = false
      this.usdTarget.disabled = false
      this.usdTarget.readOnly = false
      this.confirmTarget.value = "0"
      return
    }
    this.confirmTarget.value = this.receipt ? "1" : "0"
    this.rateTarget.readOnly = this.receipt
    this.usdTarget.disabled = !this.receipt
    this.usdTarget.readOnly = false
    if (!this.receipt) this.recompute()
    else this.paintNote()
  }

  // Mirrors FxAllocation: applied = round6(mid * (1 + markup/100)) for
  // PayPal, mid otherwise; usd = round2(eur * applied).
  recompute() {
    if (!this.foreign() || this.receipt) return
    const eur = parseFloat(this.foreignTarget.value) || 0
    const mid = parseFloat(this.rateTarget.value) || 0
    const pct = this.paypal() ? (parseFloat(this.markupValue) || 0) : 0
    const applied = Math.round(mid * (1 + pct / 100) * 1e6 + 1e-9) / 1e6
    const usd = Math.round(eur * applied * 100 + 1e-9) / 100
    this.usdTarget.value = mid > 0 && eur > 0 ? usd.toFixed(2) : ""
    this.usdTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.paintNote({ eur, mid, pct, applied, usd })
  }

  paintNote(calc) {
    const eur = calc ? calc.eur : (parseFloat(this.foreignTarget.value) || 0)
    const dateNote = this.rateDateValue ? ` for ${this.humanDate(this.rateDateValue)}` : ""
    if (this.receipt) {
      const usd = parseFloat(this.usdTarget.value) || 0
      const rate = eur > 0 && usd > 0 ? (usd / eur).toFixed(6) : null
      this.noteTarget.textContent = rate
        ? `€${eur.toFixed(2)} = $${usd.toFixed(2)}, rate ${rate} (from your receipt).`
        : "Type the USD amount PayPal actually charged, from your receipt."
      this.receiptLinkTarget.textContent = "Back to the estimate"
      return
    }
    this.receiptLinkTarget.textContent = "Enter the exact USD instead"
    if (!calc || !(calc.mid > 0)) {
      this.noteTarget.textContent = "The exchange rate is not available right now. Type the rate (USD per 1 EUR), or enter the exact USD from your receipt."
      return
    }
    if (!(calc.eur > 0)) {
      this.noteTarget.textContent = `ECB rate ${calc.mid.toFixed(4)}${dateNote}.${calc.pct > 0 ? ` PayPal adds ${calc.pct}% on top.` : ""}`
      return
    }
    if (calc.pct > 0) {
      const mid = Math.round(calc.eur * calc.mid * 100 + 1e-9) / 100
      this.noteTarget.textContent = `Est. PayPal $${calc.usd.toFixed(2)} (ECB rate ${calc.mid.toFixed(4)}${dateNote} plus ${calc.pct}% for PayPal's conversion; mid-market $${mid.toFixed(2)}). You can enter the exact USD from your PayPal receipt later.`
    } else {
      this.noteTarget.textContent = `$${calc.usd.toFixed(2)} at ${calc.mid.toFixed(4)} (ECB rate${dateNote}).`
    }
  }

  humanDate(iso) {
    const d = new Date(iso + "T00:00:00")
    return isNaN(d) ? iso : d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
  }
}
